import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSectionTabs } from '../../context/SectionTabs';

/**
 * The section's tabs, rendered inside the header.
 *
 * Hand-rolled rather than an antd <Tabs>: that component brings a content
 * area, an ink bar positioned against its own container and a stack of
 * paddings built for a page body. All that is needed here is a row of
 * buttons that knows which one is current.
 *
 * Scrolls horizontally instead of wrapping — Website & App has seven tabs,
 * and a second row would undo the height this header just gained.
 */
const HeaderTabs = ({ theme }) => {
    const { section } = useSectionTabs();
    const navigate = useNavigate();

    if (!section || !section.tabs?.length) return null;

    const isDark = theme === 'dark';
    const activeBg = '#29b8e3';
    const idleColor = isDark ? '#9AA6BF' : '#5E5F61';
    const hoverBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';

    return (
        <div
            className="header-tabs"
            // The scrollbar is hidden rather than styled: it would sit across
            // the header's bottom edge and read as a border that is only
            // sometimes there.
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                flex: 1,
                minWidth: 0,
                margin: '0 16px',
            }}
        >
            {section.tabs.map((tab) => {
                const active = tab.key === section.active;
                return (
                    <button
                        key={tab.key}
                        type="button"
                        onClick={() => navigate(`${section.basePath}?tab=${tab.key}`)}
                        aria-current={active ? 'page' : undefined}
                        style={{
                            flexShrink: 0,
                            border: 'none',
                            cursor: 'pointer',
                            borderRadius: 8,
                            padding: '6px 12px',
                            fontSize: 13,
                            fontWeight: active ? 600 : 500,
                            lineHeight: 1.4,
                            whiteSpace: 'nowrap',
                            transition: 'background 0.15s, color 0.15s',
                            background: active ? activeBg : 'transparent',
                            color: active ? '#fff' : idleColor,
                        }}
                        onMouseEnter={(e) => {
                            if (!active) e.currentTarget.style.background = hoverBg;
                        }}
                        onMouseLeave={(e) => {
                            if (!active) e.currentTarget.style.background = 'transparent';
                        }}
                    >
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
};

export default HeaderTabs;
